/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI Cloud (C) Copyright IBM Corp. 2015, 2021
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */
define([
	'./Button',
	'../AuthoringToolbar',
	'require',
	'jquery',
	'react',
	'react-dom',
	'./NextViewComponent'
], function(View, AuthoringToolbar, require, $, React, ReactDOM, NextViewComponent) {

	/**
	 * Represents a drop down menu that can be added to an app bar
	 */
	var NextViewToolbarComponent = View.extend({
		events: {
			'primaryaction': '_handleClick'
		},
		baseClass: 'toolbar',
		init: function(itemSpec, parentItem, toolbar) {
			NextViewToolbarComponent.inherited('init', this, arguments);
			this.requireFn = this.requireFn || require;
			this.toolbar = toolbar;
			this.placement = itemSpec && itemSpec.placement || this.toolbar.placement;
			this.parentItem = parentItem;
			this.toolbarItemsTriggered = {};

			this.toolbar.on('toolbar:hide', this._onToolbarHide, this);
		},

		_handleClick: function(evt) {
			if (this.$('button').prop('disabled')){
				return Promise.resolve();
			}

			return this.initializeNextView(evt);
		},

		/**
		 * This method is called from ReactToolbarContainer simulating a NextView click in the react toolbar
		 */
		initializeNextView: function(evt) {
			this.currentTarget = evt.currentTarget;
			const modulePath = this.viewModule || this.view && this.view.module;
			if (modulePath && !(this.currentTarget in this.toolbarItemsTriggered)) {
				this.toolbarItemsTriggered[this.currentTarget] = true;
				return this._loadModule(modulePath)
					.then((ViewModule) => {
						let viewOptions;
						if(this.view && this.view.state) {
							viewOptions = {
								state: this.view.state,
								actions: this._getActions(this.view.state)
							};
						} else {
							viewOptions = typeof this.viewOptions === 'function' ? this.viewOptions() : this.viewOptions;
						}

						//Initialize the viewModule to be used as the subview of the nextView 
						//(eg: TopBottomDialog or FormatDialog)
						var nextView = new ViewModule(viewOptions || {});

						if (nextView.preload) {
							return nextView.preload()
								.then(this._renderNextView.bind(this, nextView))
								.then((toolbar) => {
									if(nextView.renderCallBack){
										nextView.renderCallBack(toolbar);
									}
								});
						} else {
							this._renderNextView(nextView);
						}
					})
					// release lock on success or error
					.then((success) => {
						delete this.toolbarItemsTriggered[this.currentTarget];
						return Promise.resolve(success);
					}, (error) => {
						delete this.toolbarItemsTriggered[this.currentTarget];
						return Promise.reject(error);
					});
			}
			return Promise.resolve();
		},

		_loadModule: function(module) {
			return new Promise((resolve, reject) => {
				try {
					this.requireFn([module], resolve, reject);
				} catch(error) {
					reject(error);
				}
			});
		},

		_getActions: function(state) {
			const actions = this.actions || {};
			const flyoutActions = {};
			if (state.flyoutActions && state.flyoutActions.close) {
				flyoutActions.close = this._reactClose.bind(this);
			}
			return {
				...flyoutActions,
				...actions
			};
		},

		createFlyoutSubView() {
			return new AuthoringToolbar({
				container: $('body'),
				placement: 'bottom',
				triggerNode: this.currentTarget,
				cbOnPopupClosed: () => this._onFlyoutSubviewClosed()
			});
		},

		_onFlyoutSubviewClosed() {
			this.toolbar.setActiveFlyoutView(null);
		},

		//Render the nextView which means setting up data into the toolbar/flyout for rendering the heading
		//and adding a spec to the toolbar which contains the subView for the action (eg: TopBottomDialog) of the nextView.
		_renderNextView: function(nextView) {
			const fCreateAndShowSubView = (toolbar, stackView) => {
				return new Promise((resolve, reject) => {
					toolbar.clearItems();

					if (this.legacyNextView) {
						const showCaption = this.showCaption === undefined ? true : this.showCaption;
						const name = this._getViewCaption();

						//Rendering the heading section in the legacy nextView (deprecated) is done by setting up options in the toolbar
						//and rendering using JQuery in AuthoringToolbar.getRenderedHtml().
						toolbar.setName(name);
						toolbar.setShowCaption(showCaption);

						if (stackView) {
							toolbar.enableGoPrevious(this.enableGoPrevious);
							if (this.enableClose) {
								toolbar.enableClose(true);
							}
						} else {
							toolbar.enableClose(true);
						}
					} else {
						//For REACT, nextView handles its own rendering of the header using the NextViewComponent.
						toolbar.setReactHeadingRenderer(this._reactRender.bind(this, stackView));
					}

					return toolbar
						.addItems([{
							name: 'subview',
							responsive: false,
							editable: false,
							changedAction: null,
							subView: nextView,
							type: 'SubView'
						}])
						.then(() => {
							toolbar.show(this.placement);
						})
						.then(() => {
							resolve(toolbar);
						})
						.catch(reject);
				});
			};

			const stackableView = this._isStackableView();
			if (stackableView) {
				this.toolbar.pushItemsOnStack();

				return this.toolbar.hide(1)
					.then(() => {
						return fCreateAndShowSubView(this.toolbar, stackableView);
					});
			} else {
				if (this.flyoutSubView) {
					return this.flyoutSubView.hide()
						.then(() => {
							return fCreateAndShowSubView(this.flyoutSubView, stackableView);
						});
				} else {
					this.flyoutSubView = this.createFlyoutSubView();
					if (this.update) {
						this.flyoutSubView.on('toolbar:hide', () => {
							this.toolbar.updateButtons();
						});
					}
					return fCreateAndShowSubView(this.flyoutSubView, stackableView);
				}
			}
		},

		_getViewCaption: function() {
			//If a specific viewCaption has been set, use that as the title, otherwise
			//fallback to a combination of slotName and label.
			return this.viewCaption || ((this.slotName) ? `${this.label} - ${this.slotName}` : this.label);
		},

		//Render the heading of this nextView as a react component.
		_reactRender: function(isStackView, domNode) {
			//StackViews (nextViews which are children of menu items) generally need a heading to report their context.
			//non-stack views (ie: the content toolbar) generally do not have a context or need a close button.
			//showHeading in the itemSpec can override this default behaviour (to show heading/close button only for stack views)
			const showHeading = (this.showHeading===undefined) ? isStackView : this.showHeading;

			if (showHeading) {
				this._reactNode = domNode;
				const fnClose = this._reactClose.bind(this);	//The close button handler.

				//NOTE: - Usually targetCaption = columnName
				ReactDOM.render(
					<NextViewComponent
						name={this._getViewCaption()}
						targetCaption={this.targetCaption}
						hideFn={fnClose}
					/>,
					this._reactNode);
			}
		},

		_isStackableView() {
			return !!this.toolbar.pushItemsOnStack;
		},

		/**
		 * CLOSE BUTTON SUPPORT FOR REACT
		 * Unlike the legacy view, whose < chevron "rolls back" to the parent menu and hides the child,
		 * the REACT nextView header has a close button which should work identically to "click away" behaviour...ie: calls toolbar.remove()
		 * to remove the toolbar and any stacked parent toolbars.
		 * 
		 * NOTE1: The toolbar:remove event sent by remove() is used by the slot editor to properly remove the "action menu opened" blocker.
		 * NOTE2: Normally, we only have a heading/close button for "stackable views" (eg the action menu) but we support the "showHeading"
		 * 			itemSpec option which should remove the flyout only in the case of non-stackable views.
		 */
		_reactClose: function() {
			const toolbar = this._isStackableView() ? this.toolbar : this.flyoutSubView;
			toolbar.hide().then(
				() => toolbar.trigger('toolbar:remove', this._options)
			);
		},

		//The event handler responds to the 'toolbar:hide' event when a user clicks outside the view to close the toolbar.
		_onToolbarHide() {
			this._reactUnMount();
		},

		_reactUnMount: function() {
			if (this._reactNode) {
				ReactDOM.unmountComponentAtNode(this._reactNode);
				this._reactNode = null;
			}
		},

		remove: function() {
			this.toolbar.off('toolbar:hide', this._onToolbarHide, this);

			this._reactUnMount();
			if (this.flyoutSubView && this.flyoutSubView.isOpened) {
				this.flyoutSubView.hide();
			}
			NextViewToolbarComponent.inherited('remove', this, arguments);
		}
	});

	return NextViewToolbarComponent;
});
